// 10 beginner-friendly Java concept lessons with quiz + related problems

export const conceptsData = [
  {
    id: 'variables',
    order: 1,
    title: 'Variables & Data Types',
    subtitle: 'The building blocks of every Java program',
    icon: 'Type',
    difficulty: 'Beginner',
    estimatedTime: '8 min',
    concept: 'Learn how Java stores information using different data types',
    explanation: `In Java, every piece of data has a type. Primitive types (int, double, boolean, char) store simple values directly. Reference types (String, arrays, objects) store references to data in memory. Variables must be declared with their type before use.`,
    keyPoints: [
      'int stores whole numbers (-2^31 to 2^31 - 1)',
      'double stores decimal numbers with high precision',
      'boolean stores true or false',
      'char stores a single character in single quotes',
      'String stores text in double quotes (reference type)'
    ],
    codeExamples: [
      {
        title: 'Declaring different types',
        code: `int age = 25;
double price = 99.99;
boolean isReady = true;
char grade = 'A';
String name = "Kapil";

System.out.println(name + " is " + age + " years old");`,
        output: 'Kapil is 25 years old'
      },
      {
        title: 'Type casting',
        code: `int wholeNumber = 10;
double decimal = wholeNumber;  // Automatic widening
double pi = 3.14;
int truncated = (int) pi;      // Explicit narrowing
System.out.println(truncated);  // Prints 3`,
        output: '3'
      }
    ],
    quiz: [
      {
        question: 'Which data type would you use to store the value 3.14?',
        options: ['int', 'double', 'boolean', 'char'],
        correctIndex: 1,
        explanation: 'double stores decimal numbers. int only stores whole numbers, so 3.14 would be truncated to 3.'
      },
      {
        question: 'What is the correct way to declare a String variable?',
        options: [
          'string name = "Kapil";',
          'String name = \'Kapil\';',
          'String name = "Kapil";',
          'str name = "Kapil";'
        ],
        correctIndex: 2,
        explanation: 'String (capital S) is the correct type, and Strings use double quotes. Single quotes are only for char.'
      },
      {
        question: 'What is the value of x after: int x = (int) 9.8;',
        options: ['9', '10', '9.8', 'Error'],
        correctIndex: 0,
        explanation: 'Casting to int truncates (drops the decimal part). It does not round, so 9.8 becomes 9.'
      }
    ],
    relatedProblems: ['linear-search', 'factorial']
  },
  {
    id: 'operators',
    order: 2,
    title: 'Operators & Expressions',
    subtitle: 'Do math and comparisons in Java',
    icon: 'Calculator',
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    concept: 'Arithmetic, comparison, and logical operators',
    explanation: `Operators let you manipulate values. Arithmetic operators (+, -, *, /, %) perform math. Comparison operators (==, !=, <, >, <=, >=) return boolean results. Logical operators (&&, ||, !) combine booleans. The modulo (%) operator gives the remainder — very useful in DSA.`,
    keyPoints: [
      '/ is integer division when both operands are int (e.g., 7/2 = 3)',
      '% (modulo) gives remainder (e.g., 7 % 2 = 1)',
      '== compares primitives; use .equals() for Strings',
      '&& (AND) and || (OR) short-circuit for efficiency'
    ],
    codeExamples: [
      {
        title: 'Arithmetic and modulo',
        code: `int a = 17;
int b = 5;
System.out.println(a + b);   // 22
System.out.println(a - b);   // 12
System.out.println(a * b);   // 85
System.out.println(a / b);   // 3 (integer division)
System.out.println(a % b);   // 2 (remainder)`,
        output: '22\n12\n85\n3\n2'
      },
      {
        title: 'Comparison and logic',
        code: `int age = 20;
boolean canVote = age >= 18;         // true
boolean isTeen = age >= 13 && age <= 19;  // false
boolean invalid = age < 0 || age > 150;   // false
System.out.println("Can vote: " + canVote);`,
        output: 'Can vote: true'
      }
    ],
    quiz: [
      {
        question: 'What is the value of 15 % 4?',
        options: ['3', '3.75', '4', '11'],
        correctIndex: 0,
        explanation: '15 % 4 gives the remainder when 15 is divided by 4. 15 = 4 × 3 + 3, so remainder is 3.'
      },
      {
        question: 'What does 10 / 3 return in Java when both are int?',
        options: ['3.33', '3', '4', 'Error'],
        correctIndex: 1,
        explanation: 'Integer division truncates the decimal. 10 / 3 = 3.33, but Java returns 3.'
      },
      {
        question: 'What is the result of: true && (false || true)?',
        options: ['true', 'false', 'Error', 'null'],
        correctIndex: 0,
        explanation: '(false || true) is true. Then true && true is true. Parentheses evaluated first, then && applied.'
      }
    ],
    relatedProblems: ['first-missing-positive', 'container-with-most-water']
  },
  {
    id: 'if-else',
    order: 3,
    title: 'Conditional Statements',
    subtitle: 'Make decisions in your code',
    icon: 'GitBranch',
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    concept: 'if, else if, else, and ternary',
    explanation: `Conditional statements let your program take different paths based on conditions. Use if to check a condition, else if for additional checks, and else for the default case. The ternary operator (condition ? valueIfTrue : valueIfFalse) is a compact one-line if-else.`,
    keyPoints: [
      'if evaluates a boolean expression',
      'Only ONE branch of if/else if/else executes',
      'Order matters — first true branch wins',
      'Ternary is great for simple assignments'
    ],
    codeExamples: [
      {
        title: 'Grading system',
        code: `int score = 85;
String grade;

if (score >= 90) {
    grade = "A";
} else if (score >= 80) {
    grade = "B";
} else if (score >= 70) {
    grade = "C";
} else {
    grade = "F";
}
System.out.println("Grade: " + grade);`,
        output: 'Grade: B'
      },
      {
        title: 'Ternary operator',
        code: `int age = 20;
String status = (age >= 18) ? "Adult" : "Minor";
System.out.println(status);

int a = 5, b = 3;
int max = (a > b) ? a : b;
System.out.println("Max: " + max);`,
        output: 'Adult\nMax: 5'
      }
    ],
    quiz: [
      {
        question: 'Given int x = 10; What does this print?\nif (x > 5) System.out.println("A");\nelse if (x > 8) System.out.println("B");',
        options: ['A', 'B', 'A B', 'Nothing'],
        correctIndex: 0,
        explanation: 'x > 5 is true first, so "A" prints. else if never runs when a previous branch matched.'
      },
      {
        question: 'What does this return? int y = (7 % 2 == 0) ? 100 : 200;',
        options: ['100', '200', '7', '0'],
        correctIndex: 1,
        explanation: '7 % 2 = 1, which is not equal to 0. So the condition is false, returning 200.'
      },
      {
        question: 'Which statement is TRUE about if-else?',
        options: [
          'All branches always execute',
          'Only one branch executes',
          'else is required',
          'if can only check numbers'
        ],
        correctIndex: 1,
        explanation: 'Only the first matching branch runs. else is optional. if can check any boolean expression.'
      }
    ],
    relatedProblems: ['linear-search', 'binary-search']
  },
  {
    id: 'for-loop',
    order: 4,
    title: 'For Loops',
    subtitle: 'Repeat code a fixed number of times',
    icon: 'Repeat',
    difficulty: 'Beginner',
    estimatedTime: '12 min',
    concept: 'Iteration with counter-based loops',
    explanation: `A for loop repeats a block of code. It has 3 parts: initialization (runs once), condition (checked before each iteration), update (runs after each iteration). For loops are ideal when you know how many times to repeat, like iterating over arrays.`,
    keyPoints: [
      'for (init; condition; update) { body }',
      'Initialization typically declares a counter (int i = 0)',
      'Condition must be true to continue',
      'Update usually increments/decrements the counter'
    ],
    codeExamples: [
      {
        title: 'Basic counting',
        code: `for (int i = 1; i <= 5; i++) {
    System.out.println("Count: " + i);
}`,
        output: 'Count: 1\nCount: 2\nCount: 3\nCount: 4\nCount: 5'
      },
      {
        title: 'Sum an array',
        code: `int[] nums = {10, 20, 30, 40};
int sum = 0;
for (int i = 0; i < nums.length; i++) {
    sum += nums[i];
}
System.out.println("Sum: " + sum);`,
        output: 'Sum: 100'
      },
      {
        title: 'Enhanced for-each',
        code: `int[] nums = {1, 2, 3};
for (int n : nums) {
    System.out.println(n * 2);
}`,
        output: '2\n4\n6'
      }
    ],
    quiz: [
      {
        question: 'How many times does this loop run?\nfor (int i = 0; i < 10; i++) { ... }',
        options: ['9', '10', '11', 'Infinite'],
        correctIndex: 1,
        explanation: 'i goes from 0 to 9 (10 values total). The loop stops when i becomes 10 because i < 10 is false.'
      },
      {
        question: 'What is the final value of sum?\nint sum = 0;\nfor (int i = 1; i <= 4; i++) sum += i;',
        options: ['4', '6', '10', '15'],
        correctIndex: 2,
        explanation: 'sum = 0+1+2+3+4 = 10. i takes values 1, 2, 3, 4 before the condition fails at i=5.'
      },
      {
        question: 'Which loop iterates through every element of an array?',
        options: [
          'for (int i = 0; i < arr.length; i++)',
          'for (int i = 1; i < arr.length; i++)',
          'for (int i = 0; i <= arr.length; i++)',
          'for (int i = 0; i < arr.length - 1; i++)'
        ],
        correctIndex: 0,
        explanation: 'Array indices go from 0 to length-1. Starting at 0 and ending when i < length covers all elements.'
      }
    ],
    relatedProblems: ['linear-search', 'bubble-sort', 'product-except-self']
  },
  {
    id: 'while-loop',
    order: 5,
    title: 'While Loops',
    subtitle: 'Loop until a condition changes',
    icon: 'RefreshCw',
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    concept: 'Condition-based iteration',
    explanation: `A while loop repeats as long as a condition is true. It's ideal when you don't know how many iterations you need in advance — for example, binary search halving the range, or two pointers moving toward each other. Always ensure the condition eventually becomes false to avoid infinite loops.`,
    keyPoints: [
      'while (condition) { body } — checked before each iteration',
      'do { body } while (condition); — body runs at least once',
      'Update the loop variable inside the body',
      'Watch for infinite loops!'
    ],
    codeExamples: [
      {
        title: 'Countdown',
        code: `int count = 5;
while (count > 0) {
    System.out.println(count);
    count--;
}
System.out.println("Liftoff!");`,
        output: '5\n4\n3\n2\n1\nLiftoff!'
      },
      {
        title: 'Two pointer example',
        code: `int[] arr = {1, 2, 3, 4, 5};
int left = 0;
int right = arr.length - 1;
while (left < right) {
    System.out.println(arr[left] + " and " + arr[right]);
    left++;
    right--;
}`,
        output: '1 and 5\n2 and 4'
      }
    ],
    quiz: [
      {
        question: 'How many times does this print?\nint i = 0;\nwhile (i < 3) { System.out.println("Hi"); i++; }',
        options: ['2', '3', '4', 'Infinite'],
        correctIndex: 1,
        explanation: 'i takes values 0, 1, 2 (3 iterations). At i=3, the condition i<3 is false and loop exits.'
      },
      {
        question: 'What is the KEY difference between while and do-while?',
        options: [
          'while is faster',
          'do-while runs the body at least once even if condition is false',
          'do-while cannot use counters',
          'while checks condition after body'
        ],
        correctIndex: 1,
        explanation: 'do-while checks the condition AFTER running the body, so the body always executes at least once.'
      },
      {
        question: 'Which mistake creates an infinite loop?\nint i = 5;\nwhile (i > 0) { System.out.println(i); }',
        options: [
          'Missing i-- inside the loop',
          'i should start at 0',
          'Condition should be i < 0',
          'Nothing wrong'
        ],
        correctIndex: 0,
        explanation: 'i is never modified inside the loop, so it stays 5 forever, and the condition never becomes false.'
      }
    ],
    relatedProblems: ['binary-search', 'container-with-most-water', 'remove-duplicates']
  },
  {
    id: 'arrays',
    order: 6,
    title: 'Arrays',
    subtitle: 'Store multiple values in one variable',
    icon: 'Grid3x3',
    difficulty: 'Beginner',
    estimatedTime: '12 min',
    concept: 'Fixed-size ordered collections',
    explanation: `An array holds multiple values of the same type in a fixed-size, ordered structure. Access elements by their zero-based index (arr[0] is the first, arr[length-1] is the last). Arrays are the foundation of most DSA problems.`,
    keyPoints: [
      'int[] arr = new int[5]; — creates array of size 5',
      'int[] arr = {1, 2, 3}; — array literal',
      'arr.length gives the size (not a method — no parentheses)',
      'Accessing arr[arr.length] causes ArrayIndexOutOfBoundsException'
    ],
    codeExamples: [
      {
        title: 'Create and access',
        code: `int[] scores = {85, 92, 78, 95, 88};
System.out.println(scores[0]);        // 85
System.out.println(scores[4]);        // 88
System.out.println(scores.length);    // 5

// Modify
scores[2] = 80;
System.out.println(scores[2]);        // 80`,
        output: '85\n88\n5\n80'
      },
      {
        title: 'Iterate and find max',
        code: `int[] nums = {3, 7, 2, 9, 5};
int max = nums[0];
for (int i = 1; i < nums.length; i++) {
    if (nums[i] > max) {
        max = nums[i];
    }
}
System.out.println("Max: " + max);`,
        output: 'Max: 9'
      }
    ],
    quiz: [
      {
        question: 'Given int[] arr = {10, 20, 30, 40}; what is arr[2]?',
        options: ['10', '20', '30', '40'],
        correctIndex: 2,
        explanation: 'Arrays are 0-indexed. arr[0]=10, arr[1]=20, arr[2]=30, arr[3]=40.'
      },
      {
        question: 'What is arr.length for int[] arr = new int[7];?',
        options: ['6', '7', '8', 'Error'],
        correctIndex: 1,
        explanation: 'length returns the size of the array (7), regardless of whether the elements have been assigned.'
      },
      {
        question: 'What happens with arr[arr.length]?',
        options: [
          'Returns the last element',
          'Returns 0',
          'ArrayIndexOutOfBoundsException',
          'Returns null'
        ],
        correctIndex: 2,
        explanation: 'Valid indices are 0 to length-1. Accessing at index length throws ArrayIndexOutOfBoundsException.'
      }
    ],
    relatedProblems: ['linear-search', 'product-except-self', 'first-missing-positive', 'bubble-sort']
  },
  {
    id: 'methods',
    order: 7,
    title: 'Methods (Functions)',
    subtitle: 'Reusable blocks of code',
    icon: 'Code',
    difficulty: 'Beginner',
    estimatedTime: '12 min',
    concept: 'Define, call, and return values',
    explanation: `Methods (also called functions) let you encapsulate logic to be reused. A method has a return type, name, parameters, and body. Use void when the method doesn't return anything. Methods make code cleaner and enable DSA patterns like recursion.`,
    keyPoints: [
      'Signature: returnType methodName(paramType paramName, ...)',
      'return sends a value back and exits the method',
      'void methods don\'t return a value',
      'Parameters are like inputs to the method'
    ],
    codeExamples: [
      {
        title: 'A simple method',
        code: `public class Main {
    static int add(int a, int b) {
        return a + b;
    }
    
    public static void main(String[] args) {
        int sum = add(5, 3);
        System.out.println(sum);  // 8
    }
}`,
        output: '8'
      },
      {
        title: 'void method',
        code: `static void greet(String name) {
    System.out.println("Hello, " + name + "!");
}

// call it
greet("Kapil");
greet("World");`,
        output: 'Hello, Kapil!\nHello, World!'
      }
    ],
    quiz: [
      {
        question: 'What does the return keyword do?',
        options: [
          'Restarts the method',
          'Sends a value back and exits the method',
          'Prints a value',
          'Loops the method'
        ],
        correctIndex: 1,
        explanation: 'return both provides the output value and immediately exits the method.'
      },
      {
        question: 'What return type do you use if a method doesn\'t return anything?',
        options: ['null', 'empty', 'void', 'None'],
        correctIndex: 2,
        explanation: 'void means "no return value". You can\'t use return with a value in a void method.'
      },
      {
        question: 'Given: int multiply(int a, int b) { return a * b; }\nWhat is multiply(4, 6)?',
        options: ['10', '24', '46', '2'],
        correctIndex: 1,
        explanation: 'The method returns a * b = 4 * 6 = 24.'
      }
    ],
    relatedProblems: ['factorial', 'linear-search']
  },
  {
    id: 'classes-objects',
    order: 8,
    title: 'Classes & Objects',
    subtitle: 'Object-Oriented Programming basics',
    icon: 'Box',
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    concept: 'Blueprint (class) and instance (object)',
    explanation: `A class is a blueprint describing what data (fields) and behavior (methods) an object should have. An object is a specific instance created from that class. Every DSA solution here uses the "Solution" class pattern. new ClassName() creates a new object.`,
    keyPoints: [
      'class defines the blueprint',
      'new creates an object from the class',
      'Fields (variables) hold each object\'s data',
      'Methods define what objects can do',
      'Use the dot operator (.) to access fields/methods'
    ],
    codeExamples: [
      {
        title: 'Define a class',
        code: `class Student {
    String name;
    int age;
    
    void greet() {
        System.out.println("Hi, I'm " + name);
    }
}`,
        output: '// Blueprint defined'
      },
      {
        title: 'Create and use objects',
        code: `Student s1 = new Student();
s1.name = "Kapil";
s1.age = 20;
s1.greet();  // "Hi, I'm Kapil"

Student s2 = new Student();
s2.name = "Aayush";
s2.greet();  // "Hi, I'm Aayush"`,
        output: "Hi, I'm Kapil\nHi, I'm Aayush"
      }
    ],
    quiz: [
      {
        question: 'What is the relationship between a class and an object?',
        options: [
          'They are the same thing',
          'A class is a blueprint; objects are instances',
          'An object is a blueprint; classes are instances',
          'Neither exists in Java'
        ],
        correctIndex: 1,
        explanation: 'The class defines what an object looks like. You create actual objects (instances) from the class using new.'
      },
      {
        question: 'Which keyword creates a new object?',
        options: ['create', 'new', 'make', 'instantiate'],
        correctIndex: 1,
        explanation: 'The new keyword allocates memory and creates a new instance of the specified class.'
      },
      {
        question: 'How do you access a field of an object called car?',
        options: ['car:field', 'car->field', 'car.field', 'car[field]'],
        correctIndex: 2,
        explanation: 'Java uses the dot operator to access fields and methods of an object.'
      }
    ],
    relatedProblems: ['linear-search', 'binary-search']
  },
  {
    id: 'recursion-basics',
    order: 9,
    title: 'Recursion Basics',
    subtitle: 'A method that calls itself',
    icon: 'Layers',
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    concept: 'Base case + recursive case',
    explanation: `Recursion is when a method calls itself to solve a smaller version of the same problem. Every recursive method needs (1) a BASE CASE that stops the recursion, and (2) a RECURSIVE CASE that reduces the problem size. Without a base case, recursion never stops (StackOverflowError!).`,
    keyPoints: [
      'Base case: the simplest input, solved directly',
      'Recursive case: calls itself with smaller/simpler input',
      'Each call adds a frame to the call stack',
      'Great for problems that split into similar subproblems'
    ],
    codeExamples: [
      {
        title: 'Factorial (n!)',
        code: `static int factorial(int n) {
    // Base case
    if (n <= 1) return 1;
    // Recursive case
    return n * factorial(n - 1);
}

System.out.println(factorial(5));  // 120`,
        output: '120'
      },
      {
        title: 'Sum from 1 to n',
        code: `static int sum(int n) {
    if (n == 0) return 0;      // base
    return n + sum(n - 1);     // recursive
}
System.out.println(sum(10));   // 55`,
        output: '55'
      }
    ],
    quiz: [
      {
        question: 'What is missing from a recursive method that never terminates?',
        options: [
          'A return statement',
          'A base case',
          'A loop',
          'A parameter'
        ],
        correctIndex: 1,
        explanation: 'Without a base case, the method keeps calling itself indefinitely, causing a StackOverflowError.'
      },
      {
        question: 'For factorial(4), how many recursive calls (excluding the initial call)?',
        options: ['3', '4', '5', '24'],
        correctIndex: 0,
        explanation: 'factorial(4) → factorial(3) → factorial(2) → factorial(1) [base]. That\'s 3 recursive calls after the initial.'
      },
      {
        question: 'Which is TRUE about recursion?',
        options: [
          'Recursion is always faster than loops',
          'Every recursion can be written as a loop',
          'Recursion doesn\'t use memory',
          'Recursion cannot return values'
        ],
        correctIndex: 1,
        explanation: 'Any recursion can be converted to an iterative loop (sometimes using a stack). Recursion uses stack memory for each call.'
      }
    ],
    relatedProblems: ['factorial', 'fibonacci', 'tower-of-hanoi']
  },
  {
    id: 'strings',
    order: 10,
    title: 'String Manipulation',
    subtitle: 'Work with text data',
    icon: 'FileText',
    difficulty: 'Beginner',
    estimatedTime: '12 min',
    concept: 'String methods and immutability',
    explanation: `Strings in Java are objects, not primitives. Once created, a String cannot be changed — it is immutable. Operations that "modify" a String actually return a new String. Access characters with .charAt(i), get length with .length(), and compare with .equals() (never ==).`,
    keyPoints: [
      'Strings are immutable (unchangeable)',
      'Length: str.length() — with parentheses (unlike arrays)',
      'Char at index: str.charAt(i)',
      'Compare content: str.equals(other) — NOT ==',
      'Concatenate with + or .concat()'
    ],
    codeExamples: [
      {
        title: 'Basic operations',
        code: `String s = "Hello Kapil";
System.out.println(s.length());       // 11
System.out.println(s.charAt(6));      // 'K'
System.out.println(s.toUpperCase());  // HELLO KAPIL
System.out.println(s.substring(6));   // Kapil`,
        output: '11\nK\nHELLO KAPIL\nKapil'
      },
      {
        title: 'Comparison',
        code: `String a = "hello";
String b = "hello";
System.out.println(a == b);           // may be true (interned)
System.out.println(a.equals(b));      // true — use this!

String c = new String("hello");
System.out.println(a == c);           // false
System.out.println(a.equals(c));      // true`,
        output: 'true\ntrue\nfalse\ntrue'
      }
    ],
    quiz: [
      {
        question: 'What does "Java".charAt(1) return?',
        options: ["'J'", "'a'", "'v'", "'J' as int"],
        correctIndex: 1,
        explanation: 'charAt is 0-indexed. "Java"[0]=J, [1]=a, [2]=v, [3]=a. So charAt(1) is \'a\'.'
      },
      {
        question: 'Which is the correct way to compare String contents?',
        options: [
          's1 == s2',
          's1.compare(s2)',
          's1.equals(s2)',
          's1.same(s2)'
        ],
        correctIndex: 2,
        explanation: '.equals() compares actual content. == compares references (may give false even for equal strings).'
      },
      {
        question: 'What does "Java" + 5 evaluate to?',
        options: ['9', '"Java5"', 'Error', '"Java 5"'],
        correctIndex: 1,
        explanation: 'When + involves a String, Java performs string concatenation. The int 5 is converted to "5" and joined.'
      }
    ],
    relatedProblems: ['lcs']
  }
];

export const getConceptById = (id) => conceptsData.find(c => c.id === id);
